import { motion } from "framer-motion";
import { Heart, Shield, Users, Target, ArrowRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const About = () => {
  const leadershipMembers = [
    {
      name: "Liz",
      role: "Founder",
      image: "/team-liz.png",
      bio: "Liz is the heart and driving force behind Three Eye Blind. A Navy veteran with a fierce passion for justice and deep love for animals, she founded the rescue to fight for the voiceless. She lives with her three rescue dogs — Boon, Indy, and Abby — and is lovingly bossed around by her three one-eyed cats: Maverick, Iceman, and Goose.",
    },
    {
      name: "Allie",
      role: "Marketing Director",
      image: "/team-allie.png",
      bio: "Allie is the creative force, IT support, and resident realist. From building the website to designing merch, she keeps the rescue looking sharp. Her pack includes Bravo, a fluffy Great Pyrenees, Ellie, a Three Eye Blind Alumni dog, and two SPCA cats, Cal and Nessie. She also fosters puppies, making her house a rotating puppy paradise.",
    },
  ];

  const volunteers = [
    {
      name: "Tarron",
      role: "Medical and Behavior Support",
      image: "/team-tarron.png",
      bio: "Tarron is a passionate animal advocate who has always felt called to make a difference. As a medical and behavior support volunteer, she brings her heart, humor, and hands-on experience to every rescue effort. At home, she cares for a rescue farm including two beloved dogs—Hank, a former bait dog, and Shelby, an overbred senior rescued from neglect in Texas.",
    },
  ];

  const values = [
    {
      icon: Heart,
      title: "Compassion First",
      description: "Every decision centers on giving each dog not just rescue—but a stable, healthy life in a forever home.",
    },
    {
      icon: Shield,
      title: "No One Left Behind",
      description: "We take on complex medical and behavioral cases that many rescues can't support. We never give up.",
    },
    {
      icon: Users,
      title: "Community Driven",
      description: "We maintain strong relationships with fosters, adopters, and veterinary partners for successful placements.",
    },
    {
      icon: Target,
      title: "Transparency",
      description: "We operate with urgency, transparency, and integrity in every case we take on.",
    },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-20">
          <img
            src="/about-hero-dog.jpg"
            alt="Rescue dog"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              About Us
            </span>
            <h1 className="font-['Bebas_Neue'] text-6xl lg:text-8xl text-white mb-6">
              Fighting For
              <span className="text-[#FFD700] block">The Voiceless</span>
            </h1>
            <p className="text-xl text-[#A3A3A3] leading-relaxed">
              3 Eye Blind Animal Rescue is a 501(c)(3) nonprofit founded in 2024 by a group of misfits who wanted to save dogs from being euthanized. Their crime? Being in the shelter too long.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-24 lg:py-32 bg-[#1A1C18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
                The Origin Story
              </span>
              <h2 className="font-['Bebas_Neue'] text-5xl text-white mb-6">
                Why &ldquo;Three Eye Blind&rdquo;?
              </h2>
              <p className="text-[#A3A3A3] text-lg mb-6 leading-relaxed">
                In 2022, our founder rescued three one-eyed kittens from a California shelter. The boys are from a litter of nine who all caught an upper respiratory infection shortly after being born. For these three babes, the infection spread to an eye requiring enucleation.
              </p>
              <p className="text-[#A3A3A3] text-lg mb-6 leading-relaxed">
                As you could imagine, our founder couldn&apos;t stand the thought of separating them. So she quickly became the happy mom of three!
              </p>
              <p className="text-[#A3A3A3] text-lg leading-relaxed">
                Since our founder is a US Navy veteran who loves the movie Top Gun, these little one-eyed goofballs are named <span className="text-[#FFD700] font-semibold">Maverick, Goose, and Iceman</span>. And yes, they do feel the need—the need for speed.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="relative">
                <img
                  src="/three-cats.png"
                  alt="Maverick, Goose, and Iceman - the three one-eyed cats"
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute -bottom-6 -right-6 bg-[#FFD700] p-6">
                  <span className="font-['Bebas_Neue'] text-4xl text-black block">3</span>
                  <span className="font-mono text-xs text-black uppercase tracking-widest">One-eyed<br />Warriors</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Our Mission
            </span>
            <h2 className="font-['Bebas_Neue'] text-5xl lg:text-6xl text-white mb-6">
              What We Do
            </h2>
            <p className="text-[#A3A3A3] text-lg max-w-3xl mx-auto">
              3 Eye Blind Animal Rescue is dedicated to saving at-risk dogs from high-kill shelters, medical neglect cases, and urgent situations where time is critical. We focus not only on rescue, but on responsible placement, rehabilitation, and long-term outcomes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-tactical p-8"
              >
                <div className="w-14 h-14 bg-[#4A5320] flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-[#FFD700]" />
                </div>
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-3">{value.title}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-24 lg:py-32 bg-[#1A1C18]" id="team">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Meet The Team
            </span>
            <h2 className="font-['Bebas_Neue'] text-5xl lg:text-6xl text-white">
              Our Leadership
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {leadershipMembers.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="team-card border border-[#4A5320] bg-[#0A0A0A] overflow-hidden"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover img-grayscale"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-['Bebas_Neue'] text-3xl text-white">{member.name}</h3>
                  <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest block mb-4">
                    {member.role}
                  </span>
                  <p className="text-[#A3A3A3] text-sm leading-relaxed">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Volunteers Section */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Our Amazing Helpers
            </span>
            <h2 className="font-['Bebas_Neue'] text-5xl lg:text-6xl text-white">
              Volunteers
            </h2>
          </div>

          <div className="space-y-8 max-w-4xl mx-auto">
            {volunteers.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="flex flex-col md:flex-row gap-8 border border-[#4A5320] bg-[#0A0A0A] overflow-hidden rounded-lg"
              >
                <div className="md:w-64 flex-shrink-0">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-64 md:h-full object-cover img-grayscale"
                  />
                </div>
                <div className="p-6 flex flex-col justify-center">
                  <h3 className="font-['Bebas_Neue'] text-3xl text-white">{member.name}</h3>
                  <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest block mb-4">
                    {member.role}
                  </span>
                  <p className="text-[#A3A3A3] text-sm leading-relaxed">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-24 lg:py-32" id="partners">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Our Friends
            </span>
            <h2 className="font-['Bebas_Neue'] text-5xl lg:text-6xl text-white mb-6">
              Partners
            </h2>
            <p className="text-[#A3A3A3] text-lg max-w-2xl mx-auto">
              We love our partner businesses that help us save animals! Go check them out!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            {/* Dogtopia Rocklin */}
            <motion.a
              href="https://www.dogtopia.com/rocklin/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group block p-8 bg-white rounded-lg hover:shadow-xl transition-all duration-300 border border-[#4A5320] hover:border-[#FFD700]"
            >
              <div className="flex flex-col items-center text-center">
                <img 
                  src="/partner-dogtopia.png"
                  alt="Dogtopia Rocklin Logo"
                  className="h-32 object-contain mb-4"
                />
                <p className="text-[#666] text-sm mb-4">Dog Daycare, Boarding & Spa</p>
                <span className="inline-flex items-center gap-2 text-[#4A5320] group-hover:text-[#FFD700] font-mono text-sm uppercase tracking-widest">
                  Visit Website
                  <ExternalLink className="w-4 h-4" />
                </span>
              </div>
            </motion.a>

            {/* Paw Power Nutrition */}
            <motion.a
              href="https://pawpowernutrition.com"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group block p-8 bg-white rounded-lg hover:shadow-xl transition-all duration-300 border border-[#4A5320] hover:border-[#FFD700]"
            >
              <div className="flex flex-col items-center text-center">
                <img 
                  src="/partner-pawpower.jpg"
                  alt="Paw Power Nutrition Logo"
                  className="h-32 object-contain mb-4"
                />
                <p className="text-[#666] text-sm mb-4">Clean Dog Food & Supplements</p>
                <span className="inline-flex items-center gap-2 text-[#4A5320] group-hover:text-[#FFD700] font-mono text-sm uppercase tracking-widest">
                  Visit Website
                  <ExternalLink className="w-4 h-4" />
                </span>
              </div>
            </motion.a>
          </div>

          <div className="text-center">
            <Link
              to="/applications"
              data-testid="become-partner-button"
              className="btn-secondary inline-flex items-center gap-2"
            >
              Become a Partner
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#4A5320]/20 border-y border-[#4A5320]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Bebas_Neue'] text-5xl text-white mb-6">
            Join Our Mission
          </h2>
          <p className="text-[#A3A3A3] text-lg mb-8">
            Whether you adopt, foster, volunteer, or donate—every action makes a difference.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/applications" className="btn-primary">
              Get Involved
            </Link>
            <Link to="/donate" className="btn-secondary">
              Donate Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
